import React from 'react';
import { RFP } from '../data/mock';
import PageHero from '../components/PageHero';
import { ProcessBlock } from './SOC2Page';
import { CheckCircle2, ArrowRight, FileText, Presentation, IndianRupee, FlaskConical, Network, Repeat, FileSearch } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const ICONS = [FileText, Presentation, IndianRupee, FlaskConical, Network, Repeat];

export default function RFPPage() {
  return (
    <div>
      <PageHero eyebrow={RFP.hero.eyebrow} title={RFP.hero.title} subtitle={RFP.hero.subtitle} />

      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <span className="text-[12.5px] uppercase tracking-[0.22em] font-semibold text-[#E85A18]">Our offering</span>
            <h2 className="mt-3 text-[32px] lg:text-[42px] leading-[1.1] tracking-tight text-[#0F1B2D] font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              A full bid-desk for Med-Tech startups.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {RFP.offerings.map((o, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <div key={o.title} className="group rounded-2xl border border-slate-200/70 bg-white p-6 hover:border-orange-200 hover:bg-[#FFFBF6] hover:-translate-y-1 hover:shadow-[0_20px_50px_-25px_rgba(232,90,24,0.35)] transition-all duration-300">
                  <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#FFECD9] to-[#FFD5B4] flex items-center justify-center group-hover:from-[#FFB27A] group-hover:to-[#E85A18] transition-colors duration-300">
                    <Icon className="h-5 w-5 text-[#E85A18] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="mt-5 text-[17.5px] font-semibold text-[#0F1B2D]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {o.title}
                  </h3>
                  <p className="mt-2 text-[14px] leading-[1.65] text-slate-600">{o.copy}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <ProcessBlock title={"How we build a winning RFP response"} subtitle="A repeatable, evidence-first workflow you can hand to procurement teams with confidence." steps={RFP.process} dark />

      <section className="py-20 lg:py-24 bg-[#FDF8F2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-2">
            <span className="text-[12.5px] uppercase tracking-[0.22em] font-semibold text-[#E85A18]">Sample structure</span>
            <h2 className="mt-3 text-[30px] lg:text-[38px] leading-[1.1] tracking-tight text-[#0F1B2D] font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {RFP.sample.title}
            </h2>
            <p className="mt-4 text-[15px] leading-[1.7] text-slate-600 max-w-md">
              Every response is tailored to the buyer, but the underlying architecture is battle-tested across hospital, insurance, and government Med-Tech RFPs.
            </p>
            <Button asChild className="mt-8 rounded-full bg-[#0F1B2D] hover:bg-[#1a2942] text-white h-12 px-6">
              <Link to="/contact">Book a bid-desk consult <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
          <div className="lg:col-span-3">
            <div className="rounded-3xl bg-white border border-orange-100 shadow-[0_30px_80px_-30px_rgba(232,90,24,0.35)] p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#FFB27A] to-[#E85A18] flex items-center justify-center">
                  <FileSearch className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-[13px] uppercase tracking-widest text-slate-500 font-semibold">Response Blueprint</div>
                  <div className="text-[15px] font-semibold text-[#0F1B2D]">Med-Tech hospital RFP</div>
                </div>
              </div>
              <ol className="space-y-3">
                {RFP.sample.sections.map((s, i) => (
                  <li key={s} className="flex items-start gap-3 rounded-xl border border-orange-100/70 bg-[#FFF9F1] px-4 py-3 text-[14px] text-[#0F1B2D]">
                    <span className="text-[11px] font-mono text-[#E85A18] mt-1">{String(i + 1).padStart(2, '0')}</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-10">
            <span className="text-[12.5px] uppercase tracking-[0.22em] font-semibold text-[#E85A18]">Outcomes</span>
            <h2 className="mt-3 text-[30px] lg:text-[38px] leading-[1.1] tracking-tight text-[#0F1B2D] font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              What our clients see.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {RFP.outcomes.map((o) => (
              <div key={o} className="flex items-start gap-3 rounded-2xl border border-orange-100 bg-[#FFF9F1] p-5 text-[15px] text-slate-700">
                <CheckCircle2 className="h-5 w-5 text-[#FF7A2E] mt-0.5 flex-shrink-0" />
                <span>{o}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
